<template>
  <v-sheet rounded outlined>
    <TableSkeleton
      v-show="isLoading"
      class="px-6"
      data-testid="validationRequestContentTable-tableSkeleton"
      :columns="headers.length || 5"
      :rows="options.itemsPerPage"
    />
    <v-data-table
      v-show="!isLoading"
      data-private
      data-testid="validationRequestContentTable-tableView"
      data-tourid="validationsView-table-content"
      ref="validations-table"
      class="validations-table"
      dense
      :headers="headers"
      :footer-props="footerOptions"
      :items="rows"
      :options.sync="options"
      :server-items-length="totalItems"
      :mobile-breakpoint="$vuetify.breakpoint.thresholds.md"
    >
      <template v-slot:[`item.name`]="{ item }">
        <v-tooltip top max-width="300px">
          <template v-slot:activator="{ on }">
            <div class="d-flex align-center">
              <router-link
                style="max-width: 367px;"
                class="rev-name"
                target="_blank"
                :to="generateRevRoute(organizationId, item.entityType, item.id)"
              >
                <span
                  v-on="on"
                  v-snip="1"
                  @click="
                    goToRevTrack({ revType: item.entityType, revId: item.id })
                  "
                >
                  {{ item.name }}
                </span>
              </router-link>
            </div>
          </template>
          {{ item.name }}
        </v-tooltip>
      </template>
      <template v-slot:[`item.type`]="{ item }">
        <div class="pb-2" style="max-width: 153px">
          <EntityChip
            data-testid="validationsRequestView-chip-entityChip"
            icon
            class="mt-2 mr-1"
            :entityType="item.entityType"
          />
        </div>
      </template>
      <template v-slot:[`item.validatedBy`]="{ item }">
        <ChipAvatar v-if="item.validatedBy" small :account="item.validatedBy" />
        <p class=" blue-grey--text text--darken-3" v-else>-</p>
      </template>
      <template v-slot:[`item.requestCreatedAt`]="{ item }">
        <p
          v-if="item.requestCreatedAt"
          class="caption blue-grey--text text--darken-3 ma-0 pa-0"
          style="min-width: 90px"
        >
          {{ formatDate(item.requestCreatedAt) }}
        </p>
        <p class="blue-grey--text text--darken-3" v-else>-</p>
      </template>
      <template v-slot:[`item.validatedAt`]="{ item }">
        <p
          v-if="item.validatedAt"
          class="caption blue-grey--text text--darken-3 ma-0 pa-0"
          style="min-width: 90px"
        >
          {{ formatDate(item.validatedAt) }}
        </p>
        <p class="blue-grey--text text--darken-3" v-else>-</p>
      </template>
      <template v-slot:[`item.requestStatus`]="{ item }">
        <v-chip outlined small text-color="blue-grey">
          <span class="blue-grey--text text--darken-3">
            {{ getRequestStatus(item.requestStatus) }}
          </span>
        </v-chip>
      </template>
      <template v-slot:[`item.requestedBy`]="{ item }">
        <ChipAvatar v-if="item.requestedBy" small :account="item.requestedBy" />
        <p class=" blue-grey--text text--darken-3" v-else>-</p>
      </template>

      <template v-slot:[`item.author`]="{ item }">
        <ChipAvatar v-if="item.author" small :account="item.author" />
        <p class=" blue-grey--text text--darken-3" v-else>-</p>
      </template>
      <template v-slot:[`item.tags`]="{ item }">
        <div class="pt-2">
          <ChipList
            outlined
            validationsTable
            :totalItems="item.tags ? item.tags.length : 0"
            :items="getTags(item.tags)"
            :limit="3"
          />
        </div>
      </template>
      <template v-slot:[`item.completedAt`]="{ item }">
        <p
          class="caption blue-grey--text text--darken-3 ma-0 pa-0"
          style="min-width: 90px"
        >
          {{ formatDate(item.completedAt) }}
        </p>
      </template>
      <template v-slot:[`item.implementedAt`]="{ item }">
        <p
          class="caption blue-grey--text text--darken-3 ma-0 pa-0"
          style="min-width: 90px"
        >
          {{ formatDate(item.validations.implementation.validatedAt) }}
        </p>
      </template>

      <template v-slot:[`item.impactStartsAt`]="{ item }">
        <v-edit-dialog>
          <div
            class="d-flex align-center editable-item justify-space-between"
            style="min-width: 114px"
          >
            <p class="caption blue-grey--text text--darken-3 ma-0 pa-0">
              {{ formatDate(item.impactStartsAt) }}
            </p>
            <v-icon class="edit-icon ms-2" color="blue-grey" size="16"
              >mdi-pencil</v-icon
            >
          </div>
          <template v-slot:input>
            <DatePicker
              type="startDate"
              :startDate="getDatePickerValue(item.impactStartsAt)"
              :endDate="getDatePickerValue(item.impactEndsAt)"
              @change="handleStartDateChange($event, item.id)"
            />
          </template>
        </v-edit-dialog>
      </template>

      <template v-slot:[`item.impactEndsAt`]="{ item }">
        <v-edit-dialog>
          <div
            class="d-flex align-center editable-item justify-space-between"
            style="min-width: 114px"
          >
            <p class="caption blue-grey--text text--darken-3 ma-0 pa-0">
              {{ formatDate(item.impactEndsAt) }}
            </p>
            <v-icon class="edit-icon ms-2" color="blue-grey" size="16"
              >mdi-pencil</v-icon
            >
          </div>
          <template v-slot:input>
            <DatePicker
              type="endDate"
              :startDate="getDatePickerValue(item.impactStartsAt)"
              :endDate="getDatePickerValue(item.impactEndsAt)"
              @change="handleEndDateChange($event, item.id)"
            />
          </template>
        </v-edit-dialog>
      </template>

      <template v-slot:[`item.assignee`]="{ item }">
        <ChipAvatar v-if="item.assignee" small :account="item.assignee" />
        <p class="blue-grey--text text--darken-3" v-else>-</p>
      </template>
      <template v-slot:[`item.coach`]="{ item }">
        <ChipAvatar v-if="item.coach" small :account="item.coach" />
        <p class=" blue-grey--text text--darken-3" v-else>-</p>
      </template>

      <template v-slot:[`item.totalImpact`]="{ item }">
        <v-edit-dialog>
          <div
            class="d-flex align-center editable-item justify-space-between"
            style="min-width: 99px;"
          >
            <p
              v-if="item.totalImpact"
              class="caption blue-grey--text text--darken-3 ma-0 pa-0"
            >
              {{ item.totalImpact }}
            </p>

            <p class="blue-grey--text text--darken-3" v-else>-</p>

            <v-icon class="edit-icon ms-2" color="blue-grey" size="16"
              >mdi-pencil</v-icon
            >
          </div>
          <template v-slot:input>
            <v-text-field
              autofocus
              dense
              outlined
              hide-details
              class="ma-3 body-2"
              :value="getTotalImpactValue(item.validatedReview.totalImpact)"
              @change="handleTotalImpactChange($event, item.id)"
            />
          </template>
        </v-edit-dialog>
      </template>
      <template v-slot:[`item.totalImpactInGlobalCurrency`]="{ item }">
        <p
          v-if="item.totalImpactInGlobalCurrency"
          class="caption blue-grey--text text--darken-3 ma-0 pa-0"
        >
          {{ item.totalImpactInGlobalCurrency }}
        </p>

        <p class="blue-grey--text text--darken-3" v-else>-</p>
      </template>
      <template v-slot:[`item.viewValidation`]="{ item }">
        <div style="min-width: 56px;" class="d-flex justify-center">
          <router-link
            class="text-decoration-none"
            :to="{
              name: 'validateRev',
              params: {
                revId: item.id,
                revType: item.entityType,
              },
            }"
          >
            <v-icon size="18" color="blue-grey" class="validation-icon"
              >mdi-check-decagram</v-icon
            >
          </router-link>
        </div>
      </template>

      <template v-slot:[`item.validate`]="{ item }">
        <div
          style="min-width: 56px;"
          class="d-flex justify-center"
          data-tourid="validationsView-button-validateRev"
        >
          <router-link
            class="text-decoration-none"
            :to="{
              name: 'validateRev',
              params: {
                revId: item.id,
                revType: item.entityType,
              },
            }"
          >
            <v-icon size="18" color="blue-grey" class="validation-icon"
              >mdi-check-decagram-outline</v-icon
            >
          </router-link>
        </div>
      </template>
      <template v-slot:[`item.implement`]="{ item }">
        <div
          style="min-width: 56px;"
          class="d-flex justify-center"
          data-tourid="validationsView-button-implementRev"
        >
          <v-icon
            size="18"
            color="blue-grey"
            class="validation-icon"
            @click="openRevDrawer('implement', item)"
            >mdi-rocket-launch-outline</v-icon
          >
        </div>
      </template>
      <template v-slot:[`item.requestValidation`]="{ item }">
        <div style="min-width: 56px;" class="d-flex justify-center">
          <v-icon
            size="18"
            color="blue-grey"
            class="validation-icon"
            @click="openRevDrawer('requestValidation', item)"
            >mdi-check-decagram-outline</v-icon
          >
        </div>
      </template>
    </v-data-table>

    <ValidationDrawer
      v-if="showRevDrawer"
      :rev="currentRev"
      :visible="showRevDrawer"
      @close="showRevDrawer = false"
      @requestValidation="$emit('fetchRevs')"
      @cancelRequestValidation="$emit('fetchRevs')"
      @implementRev="$emit('fetchRevs')"
    />
  </v-sheet>
</template>

<script>
import { utils } from 'frontend-js-shared'
import revMixin from '@/mixins/revMixin.vue'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin'

import moment from 'moment'
import _ from 'lodash'

import EntityChip from '@/components/EntityChip.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import currencies from '@/locales/currencies.json'
import TableSkeleton from '@/components/TableSkeleton.vue'
import DatePicker from './DatePicker.vue'
import ChipList from '@/components/ChipList.vue'
import ValidationDrawer from './ValidationDrawer.vue'

export default {
  name: 'ValidationsRequestContentTable',

  mixins: [revMixin, mixin, trackerMixin],

  components: {
    EntityChip,
    ChipAvatar,
    TableSkeleton,
    DatePicker,
    ChipList,
    ValidationDrawer,
  },

  props: {
    organizationId: String,
    isLoading: {
      type: Boolean,
      default: true,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    revs: {
      type: Array,
      default: () => [],
    },
    currentPage: { type: Number, required: false },
    itemsPerPage: { type: Number, required: true },
    sortBy: { type: String, default: null },
    sortDesc: { type: Boolean, default: false },
    headers: {
      type: Array,
      default: () => [],
    },
    activeTab: {
      required: true,
    },
  },

  data() {
    return {
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      validationToUpdate: {},
      showRevDrawer: false,
      currentRev: {},
      currentRevType: '',
    }
  },

  mounted() {
    this.setTourIds()
  },

  computed: {
    options: {
      get() {
        return {
          page: this.currentPage || 1,
          itemsLength: this.totalItems,
          itemsPerPage: this.itemsPerPage,
          sortBy: _.isNull(this.sortBy) ? [] : [this.sortBy],
          sortDesc: _.isNull(this.sortDesc) ? [] : [this.sortDesc],
        }
      },
      set(event) {
        const pageChange = this.options.page !== event.page
        const itemsPerPageChange =
          this.options.itemsPerPage !== event.itemsPerPage
        const sortDescChange = this.options?.sortDesc[0] !== event.sortDesc[0]
        const sortByChange = this.options?.sortBy[0] !== event.sortBy[0]
        if (pageChange || itemsPerPageChange || sortDescChange || sortByChange)
          this.$emit('fetchRevs', {
            page: event.page,
            itemsPerPage: event.itemsPerPage,
            sortDesc: event.sortDesc[0],
            sortBy: event.sortBy[0],
          })
      },
    },

    rows() {
      return this.revs.map(item => {
        const rev = item
        return {
          ...rev,
          id: rev?.id ?? rev?._id,
          tags: rev?.tags ?? rev?.siteAttributes,
          requestedBy: rev?.requestCreatedBy,
          assignee: rev?.requestAssignees?.[0],
          validatedAt: rev?.validations?.financial?.validatedAt,
          validatedBy: rev?.validations?.financial?.auditor,
          impactStartsAt: rev?.impactStartsAt,
          impactEndsAt: rev?.impactEndsAt,
          totalImpact: this.getTotalImpactText({
            currency: rev?.currencyMetadata?.currentCurrency,
            impact: rev?.validatedReview?.totalImpact,
          }),
          totalImpactInGlobalCurrency: this.getTotalImpactText({
            currency: rev?.currencyMetadata?.globalCurrency,
            impact: rev?.validatedReview?.totalImpactInGlobalCurrency,
          }),
        }
      })
    },
  },

  methods: {
    getTags(tags = []) {
      return tags.map(tag => tag?.name)
    },

    goToRevTrack(rev) {
      this.trackValidationsNavigation({
        action: 'goToRev',
        revId: rev._id,
        revType: rev.type,
        activeTab:
          this.activeTab ===
          this.$constants.validationsFilters.contentTab.impact
            ? 'impactTab'
            : 'implementationTab',
      })
    },

    getTotalImpactValue(totalImpact) {
      return Math.round(totalImpact) ?? null
    },

    getDatePickerValue(date) {
      return date ? moment(date).format('YYYY-MM-DD') : null
    },

    handleTotalImpactChange: _.debounce(function(value, revId) {
      const currentRev = this.revs.find(rev => revId === rev.id)
      this.validationToUpdate = {
        revId: currentRev.id,
        revType: currentRev.type ?? currentRev.entityType,
        fieldToUpdate: { totalImpact: Number(value) },
      }

      this.$emit('updateValidatedReviewField', this.validationToUpdate)
    }, 100),

    handleStartDateChange(value, revId) {
      const currentRev = this.revs.find(rev => revId === rev.id)
      this.$emit('updateValidatedReviewField', {
        revId: currentRev.id,
        revType: currentRev.type ?? currentRev.entityType,
        fieldToUpdate: {
          impactStartsAt: utils.dates.getStartOfDay(value),
        },
      })
      this.$refs['validations-table'].$el.click()
    },

    handleEndDateChange(value, revId) {
      const currentRev = this.revs.find(rev => revId === rev.id)
      this.$emit('updateValidatedReviewField', {
        revId: currentRev.id,
        revType: currentRev.type ?? currentRev.entityType,
        fieldToUpdate: {
          impactEndsAt: utils.dates.getEndOfDay(value),
        },
      })
      this.$refs['validations-table'].$el.click()
    },

    getRequestStatus(status) {
      if (status === this.$constants.validationRequestStatus.inProgress)
        return this.$t('common.yes')
      if (status === this.$constants.validationRequestStatus.canceled)
        return this.$t('common.canceled_request')
      return this.$t('common.no')
    },

    getTotalImpactText({ impact, currency }) {
      if (!impact) return '-'

      return `
          ${currencies[currency]?.symbol_native}
          ${Number(impact).toLocaleString(navigator.language, {
            maximumFractionDigits: 2,
          })}
          ${currency}
          `
    },

    openRevDrawer(type, rev) {
      this.currentRevType = rev.entityType
      this.currentRev = rev
      this.showRevDrawer = true
      this.trackValidationsActions({
        action: `openDrawer-${type}`,
        revId: rev?.id,
        revType: this.currentRevType,
      })
    },

    formatDate(date) {
      const data = moment(date)
      if (!data.isValid()) return ''
      return data.format('ll')
    },

    setTourIds() {
      const tableHead = this.$refs['validations-table']?.$el?.querySelector?.(
        'thead'
      )
      if (!tableHead) return
      tableHead.dataset.tourid = 'validationsView-table-headers'
    },
  },
}
</script>
<style lang="scss">
.editable-item {
  &:hover {
    .edit-icon {
      display: block !important;
    }
  }
  .edit-icon {
    display: none !important;
  }
}
.validations-table {
  th {
    span {
      color: $color-blue-grey-darken1 !important;
    }
  }
  tr:hover {
    .fixed-column {
      background-color: $color-blue-grey-lighten3 !important;
    }

    background-color: $color-blue-grey-lighten3 !important;
    .chip-avatar {
      background-color: $color-blue-grey-lighten2 !important;
    }
    .validation-icon {
      color: $color-blue !important;
    }
  }
  .rev-name {
    color: $color-blue-grey-darken3;
    text-decoration: none;
    &:hover {
      color: $color-primary !important;
      text-decoration: underline !important;
    }
  }

  .fixed-column {
    position: sticky !important;
    position: -webkit-sticky !important;
    background-color: white;
    right: 0px;
    &::before {
      content: '';
      width: 8px;
      height: 100%;
      background: linear-gradient(
        90deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.1) 100%
      );
      position: absolute;
      top: 0;
      left: -8px;
    }
  }
  .v-data-table-header {
    white-space: nowrap;
  }
}
</style>
